import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '@/api/client'
import { Spinner } from '@/components/ui/spinner'
import { DevicePairingScreen } from '@/components/desktop/DevicePairingScreen'
import { getDesktopPlatform } from '@/lib/platform'

// window.ledgerDesktop is injected by the Electron preload script
const desktop = () => (window as any).ledgerDesktop as {
  getDeviceToken(): Promise<string | null>
  setDeviceToken(token: string): Promise<void>
  clearDeviceToken(): Promise<void>
}

type Phase = 'checking' | 'pairing' | 'error'

export function DesktopLoginPage({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [phase, setPhase] = useState<Phase>('checking')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const deviceAuthIdRef = useRef<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval>>()
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const handleApproved = useCallback(async (accessToken: string, deviceToken: string) => {
    api.setToken(accessToken)
    try {
      await desktop().setDeviceToken(deviceToken)
    } catch {
      // non-critical - token still in memory for this session
    }
    onAuthenticated()
  }, [onAuthenticated])

  const startPolling = useCallback((deviceAuthId: string, interval: number) => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const result = await api.deviceAuthExchange(deviceAuthId)
        if (!mountedRef.current) return

        if (result.status === 'approved' && result.access_token && result.device_token) {
          clearInterval(pollRef.current)
          await handleApproved(result.access_token, result.device_token)
        } else if (result.status === 'access_denied') {
          clearInterval(pollRef.current)
          setError('Pairing was denied. Please try again.')
          setPhase('error')
        }
        // authorization_pending → keep polling
      } catch {
        // Network error or expired - stop polling
        if (!mountedRef.current) return
        clearInterval(pollRef.current)
        setError('Pairing session expired. Please try again.')
        setPhase('error')
      }
    }, interval * 1000)
  }, [handleApproved])

  const startPairing = useCallback(async () => {
    setPhase('checking')
    setError(null)
    try {
      const platform = getDesktopPlatform() || 'unknown'
      const hostname = `Desktop (${platform === 'win32' ? 'Windows' : 'macOS'})`
      const result = await api.deviceAuthStart(hostname, platform)
      if (!mountedRef.current) return

      deviceAuthIdRef.current = result.device_auth_id
      setCode(result.user_code)
      setPhase('pairing')
      startPolling(result.device_auth_id, result.interval)
    } catch {
      if (!mountedRef.current) return
      setError('Could not start device pairing. Is the API reachable?')
      setPhase('error')
    }
  }, [startPolling])

  // On mount: check for stored device token first
  useEffect(() => {
    const init = async () => {
      try {
        const storedToken = await desktop().getDeviceToken()
        if (storedToken) {
          const result = await api.deviceAuthToken(storedToken)
          if (result.access_token) {
            api.setToken(result.access_token)
            onAuthenticated()
            return
          }
        }
      } catch {
        // Token invalid/revoked - clear it and fall through to pairing
        try { await desktop().clearDeviceToken() } catch { /* */ }
      }
      // No valid stored token - start pairing flow
      await startPairing()
    }
    init()
  }, [onAuthenticated, startPairing])

  if (phase === 'checking') {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-900">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="h-8 w-8 text-indigo-400" />
          <p className="text-sm text-slate-400">Connecting…</p>
        </div>
      </div>
    )
  }

  if (phase === 'error') {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-900">
        <div className="mx-auto max-w-md rounded-lg border border-slate-700 bg-slate-800 p-8 text-center">
          <p className="text-sm text-red-400">{error}</p>
          <button
            onClick={startPairing}
            className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <DevicePairingScreen
      code={code}
      pairBaseUrl={import.meta.env.VITE_LEDGER_APP_URL || api.apiOrigin}
      onCancel={undefined}
    />
  )
}
